// @flow
import type { Dispatch } from "redux";
import { get, find } from "lodash";
import type {
  RaceProgram,
  BettingInterest,
  RaceWagerType
} from "@tvg/types/Race";
import {
  setSelectionMatrixRequest,
  type Actions as BetActions
} from "@tvg/shared-actions/BetActions";
import {
  setQueryWithRacesAction,
  type Actions as ProgramActions
} from "@tvg/shared-actions/ProgramActions";

// bets that need reset with only 1 column
const resetBetTypes = [80, 90, 100];

const setMatrixSizeByColumnCount = (columnCount: number): string[][] =>
  columnCount >= 1 ? Array(columnCount).fill([]) : [[]];

const setMatrixSizeByLegCount = (legCount: number): string[][] =>
  legCount >= 1 ? Array(legCount).fill([]) : [[]];

const configureProgramPageByBetType = (
  columnCount: number,
  legCount: number,
  betId: number,
  matrix: string[][],
  dispatch: Dispatch<BetActions | ProgramActions | *>,
  betFromPick: boolean = false
) => {
  if (columnCount === 1) {
    if (matrix.length !== 1 || resetBetTypes.indexOf(betId) > -1) {
      dispatch(
        setSelectionMatrixRequest({
          selections: betFromPick
            ? matrix
            : setMatrixSizeByColumnCount(columnCount),
          favoriteSelections: setMatrixSizeByLegCount(legCount)
        })
      );
    }
  } else if (columnCount > 1) {
    dispatch(
      setSelectionMatrixRequest({
        selections: betFromPick
          ? matrix
          : setMatrixSizeByColumnCount(columnCount),
        favoriteSelections: setMatrixSizeByLegCount(legCount)
      })
    );
    if (legCount > 1) {
      dispatch(setQueryWithRacesAction(true));
    }
  }
};

const filterHKCoupledEntries = (
  bettingInterests: BettingInterest[]
): BettingInterest[] =>
  bettingInterests.map((bi: BettingInterest) => ({
    ...bi,
    runners: bi.runners.filter((runner) => !runner.runnerId.includes("S"))
  }));

const hasSelections = (selections: string[][] = []) =>
  !!selections.find((selection: string[]) => selection.length);

const hasBetType = (
  betTypeID: string = "",
  currentRace: RaceProgram
): boolean => {
  const wagerTypes = get(currentRace, "wagerTypes", []);
  return !!find(
    wagerTypes,
    (wagerType) => `${wagerType.type.id}` === `${betTypeID}`
  );
};

const getDefaultBetType = (
  currentRace: RaceProgram,
  defaultBetTypeID: string = "",
  logged: boolean = false
): ?RaceWagerType => {
  const wagerTypes = get(currentRace, "wagerTypes", []);
  const defaultType = get(wagerTypes, "[0]");

  if (logged) {
    const findWagerType = find(
      wagerTypes,
      (wagerType) => `${wagerType.type.id}` === `${defaultBetTypeID}`
    );
    return findWagerType || defaultType;
  }

  return defaultType;
};

export const mapSelectionsToNumber = (betSelections: string[][]): number[][] =>
  betSelections.map((selections: string[]): number[] =>
    selections.map((selection: string): number => +selection)
  );

const betSelections = {
  getDefaultBetType,
  hasSelections,
  setMatrixSizeByColumnCount,
  configureProgramPageByBetType,
  filterHKCoupledEntries,
  mapSelectionsToNumber,
  hasBetType
};

export default betSelections;
